import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import FloatingCart from '@/components/ui/FloatingCart'
import type { FillerTilesConfig } from '@/lib/kiosk-config'
import type { MenuPage, Product } from '@/lib/types'
import { fmt, isAvailable, minPrice } from '@/lib/utils'
import { useKioskStore } from '@/stores/kiosk-store'

// Category emoji fallbacks
const defaultCatIcons: Record<string, string> = {
  COMBODEAL: '🎁',
  BUFFÉ: '🍜',
  LUNCH: '🍱',
  FÖRRÄTTER: '🥟',
  SUSHI: '🍣',
  VARMRÄTTER: '🍛',
  WOK: '🥘',
  NUDLAR: '🍜',
  ROLLS: '🍣',
  BOWLS: '🥗',
  DESSERT: '🍨',
  DRYCKER: '🥤',
  'LÄSK & VATTEN': '🥤',
  'ÖL & SAKE': '🍺',
  VIN: '🍷',
  'ASIATISKA DRYCKER': '🧋',
  'NIGIRI & SASHIMI': '🍣',
  'DIM SUM': '🥟',
  TILLBEHÖR: '🍚',
  KAFFE: '☕',
}

function getPageImage(page: MenuPage): string | null {
  for (const c of page.categories) {
    for (const p of c.products) {
      if (p.properties?.imageUrl && isAvailable(p)) return p.properties.imageUrl
    }
  }
  return null
}

export default function ProductGrid() {
  const pages = useKioskStore((s) => s.pages)
  const setSelectedProduct = useKioskStore((s) => s.setSelectedProduct)
  const setStep = useKioskStore((s) => s.setStep)
  const loading = useKioskStore((s) => s.loading)
  const locale = useKioskStore((s) => s.locale)
  const remoteConfig = useKioskStore((s) => s.remoteConfig)
  const setCurrentCategoryLabel = useKioskStore((s) => s.setCurrentCategoryLabel)
  const setBackAction = useKioskStore((s) => s.setBackAction)
  const isLandscape = remoteConfig?.layoutMode === 'landscape'

  // Build catIcons and threeColCategories from remote config, falling back to defaults
  const catIcons = useMemo(() => {
    if (!remoteConfig?.categoryConfigs) return defaultCatIcons
    const merged = { ...defaultCatIcons }
    for (const [name, cfg] of Object.entries(remoteConfig.categoryConfigs)) {
      if (cfg.icon) merged[name] = cfg.icon
    }
    return merged
  }, [remoteConfig])

  const threeColCategoriesFromConfig = useMemo(() => {
    // Use threeColCategories from remote config if available
    if (remoteConfig?.threeColCategories && remoteConfig.threeColCategories.length > 0) {
      return remoteConfig.threeColCategories
    }
    // Or derive from categoryConfigs product_layout
    if (!remoteConfig?.categoryConfigs) return null
    return Object.entries(remoteConfig.categoryConfigs)
      .filter(([, cfg]) => cfg.product_layout?.includes('3col'))
      .map(([name]) => name)
  }, [remoteConfig])

  const scrollRef = useRef<HTMLDivElement>(null)
  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  // Two-level nav: null = category grid, string = show products in that category/page.
  // Om backoffice-konfig har skipCategoryGrid=true så hoppar vi rakt in på
  // "HELA MENYN"-vyn (alla produkter samlade utan kategori-mellansida).
  const skipCategoryGrid = remoteConfig?.skipCategoryGrid === true
  const [viewingPage, setViewingPage] = useState<string | null>(skipCategoryGrid ? '__all__' : null)
  const [selectedCatFilter, setSelectedCatFilter] = useState<string | null>(null)
  const [expandedCats, setExpandedCats] = useState<Record<string, boolean>>({})

  const sv = locale === 'sv'

  // Sync currentCategoryLabel + backAction to the landscape header based on current sub-view
  const setAfterProductAddedAction = useKioskStore((s) => s.setAfterProductAddedAction)

  useEffect(() => {
    if (viewingPage) {
      // Inside a page — show its label
      const pageLabel = viewingPage === '__all__' ? (sv ? 'HELA MENYN' : 'FULL MENU') : (pages.find((p) => p.id === viewingPage)?.name || '').toUpperCase()
      setCurrentCategoryLabel(pageLabel)
      if (skipCategoryGrid && viewingPage === '__all__') {
        // Back-knappen → menuTypeSelect (det enda meningsfulla "tillbaka" eftersom
        // kategorigrid:en är avstängd). Post-add-flow → ingen navigering, user
        // stannar i HELA MENYN och kan fortsätta beställa.
        setBackAction(() => {
          setStep('menuTypeSelect')
          useKioskStore.getState().clearCart()
        })
        setAfterProductAddedAction(() => {
          /* no-op: stanna kvar i HELA MENYN */
        })
      } else {
        // Vanligt läge: back-knappen + post-add går båda till kategorigrid:en
        const goToCategoryGrid = () => {
          setViewingPage(null)
          setSelectedCatFilter(null)
        }
        setBackAction(goToCategoryGrid)
        setAfterProductAddedAction(goToCategoryGrid)
      }
    } else {
      // On category grid — title is "VÄLJ KATEGORI"; back goes to menuTypeSelect (clears cart)
      setCurrentCategoryLabel(sv ? 'VÄLJ KATEGORI' : 'CHOOSE CATEGORY')
      setBackAction(() => {
        setStep('menuTypeSelect')
        useKioskStore.getState().clearCart()
      })
      setAfterProductAddedAction(null) // ingen post-add-special, fall back till backAction
    }
    return () => {
      setCurrentCategoryLabel(null)
      setBackAction(null)
      setAfterProductAddedAction(null)
    }
  }, [viewingPage, pages, sv, skipCategoryGrid, setCurrentCategoryLabel, setBackAction, setAfterProductAddedAction, setStep])

  // Categories that should show 3 columns instead of 2
  const defaultThreeColCategories = [
    'SUSHI',
    'WOK',
    'NUDLAR',
    'WOK & NUDLAR',
    'SUSHI & POKE',
    'NIGIRI & SASHIMI',
    'ROLLS',
    'DRYCKER',
    'LÄSK',
    'VATTEN',
    'ÖL',
    'SAKE',
    'VIN',
    'KAFFE',
    'ASIATISKA DRYCKER',
    'LÄSK & VATTEN',
    'ÖL & SAKE',
  ]
  const threeColCategories = threeColCategoriesFromConfig || defaultThreeColCategories
  function isThreeCol(catName: string): boolean {
    const upper = catName.toUpperCase()
    return threeColCategories.some((tc) => upper.includes(tc))
  }
  const handleProductPress = (product: Product) => {
    if (!isAvailable(product)) return
    setSelectedProduct(product)
  }

  // Scroll-hint: "there's more below" — updates when scrollRef scrolls or content changes.
  // Hides the arrow as soon as the bottom row is ≥50% visible and detects actual column
  // count from the DOM (so arrows align with real grid gaps, not just the global setting).
  const [hintState, setHintState] = useState<{
    hasMoreBelow: boolean
    cols: number
    gapPx: number
  }>({
    hasMoreBelow: false,
    cols: 3,
    gapPx: 48,
  })
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const update = () => {
      const tiles = el.querySelectorAll('.kiosk-btn')
      const last = tiles[tiles.length - 1] as HTMLElement | undefined
      const tileH = last?.offsetHeight ?? 300
      const threshold = tileH / 2
      const more = el.scrollHeight - el.scrollTop - el.clientHeight > threshold

      // Detect actual column count + gap from the last grid in view
      let cols = 1
      let gapPx = 0
      const parent = last?.parentElement
      if (parent) {
        const style = getComputedStyle(parent)
        const tpl = style.gridTemplateColumns
        if (tpl && tpl !== 'none') {
          cols = tpl.split(/\s+/).filter((s) => s.length > 0).length
        }
        const gap = parseFloat(style.columnGap || style.gap || '0')
        if (Number.isFinite(gap)) gapPx = gap
      }
      setHintState({ hasMoreBelow: more, cols, gapPx })
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(el)
    for (const child of Array.from(el.children)) ro.observe(child)
    return () => {
      el.removeEventListener('scroll', update)
      ro.disconnect()
    }
  }, [])
  const { hasMoreBelow, cols: hintCols, gapPx: hintGap } = hintState

  if (loading) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-kiosk-bg">
        <div className="kiosk-spinner" />
      </div>
    )
  }

  // Currently viewing a specific page's products
  // For __all__, create a virtual page with all categories from all pages
  const activePage = viewingPage === '__all__' ? { id: '__all__', name: 'HELA MENYN', categories: pages.flatMap((p) => p.categories) } : pages.find((p) => p.id === viewingPage)

  if (activePage) {
    const allCats = activePage.categories.filter((c) => c.products.some(isAvailable))
    const visibleCats = selectedCatFilter ? allCats.filter((c) => c.id === selectedCatFilter) : allCats

    // Visa sticky kategoribar i HELA MENYN — aktiveras via backoffice-toggle
    // (fullMenuCategoryBar). Skipasdig om det bara finns en kategori (då är
    // baren onödig).
    const showCategoryBar = remoteConfig?.fullMenuCategoryBar === true && viewingPage === '__all__' && allCats.length > 1
    const categoryBarAlign = remoteConfig?.fullMenuCategoryBarAlign === 'center' ? 'center' : 'left'

    return (
      <div className="h-full w-full flex flex-col bg-kiosk-bg relative">
        {/* Sticky kategoribar — bara i HELA MENYN-läge när toggle är på */}
        {showCategoryBar && <CategoryBar categories={allCats.map((c) => ({ id: c.id, name: c.name }))} scrollRef={scrollRef} align={categoryBarAlign} />}

        {/* Products with category separators + collapse */}
        {/* pt-3 i porträtt ger andrum så ring-2 på översta rutorna inte klipps av scroll-kanten */}
        <div ref={scrollRef} className={`flex-1 overflow-y-auto kiosk-scroll pb-28 ${isLandscape ? 'px-14 pt-10 max-w-350 w-full mx-auto' : 'px-5 pt-3'}`}>
          {visibleCats.map((cat) => {
            const products = cat.products.filter(isAvailable)
            if (products.length === 0) return null
            const isExpanded = expandedCats[cat.id] || false
            // Determine layout: per-category override > threeCol list > default
            const catUpper = cat.name.toUpperCase()
            const catLayout = remoteConfig?.categoryConfigs?.[catUpper]?.product_layout || remoteConfig?.categoryConfigs?.[cat.name]?.product_layout
            const defaultLayout = remoteConfig?.defaultProductLayout || 'card-image-2col'
            const effectiveLayout = catLayout || (isThreeCol(cat.name) ? 'card-image-3col' : defaultLayout)
            const wantsThreeCol = effectiveLayout.includes('3col') || viewingPage === '__all__'
            // Smart col logic: use 3 cols only if products fill rows nicely
            // Same column behavior in landscape and portrait — landscape just gets more breathing room
            const useThreeCol = wantsThreeCol && (products.length % 3 === 0 || products.length >= 5)
            const colCount = useThreeCol ? 3 : 2
            const cols = colCount === 3 ? 'grid-cols-3' : 'grid-cols-2'
            const isListLayout = effectiveLayout === 'list-row' || effectiveLayout === 'text-only'
            const maxRows = remoteConfig?.maxCollapsedRows ?? 1
            // For list layouts, each row = 1 item; for grids, each row = colCount items
            const collapsedCount = isListLayout ? maxRows * 3 : colCount * maxRows
            const collapseEnabled = remoteConfig?.collapseEnabled ?? true
            const showCollapse = collapseEnabled && products.length > collapsedCount
            const visibleProducts = showCollapse && !isExpanded ? products.slice(0, collapsedCount) : products
            const hasHiddenProducts = showCollapse && !isExpanded && products.length > collapsedCount
            // Peek: show partial next row (clipped) to hint there's more
            const peekProducts = hasHiddenProducts ? products.slice(collapsedCount, collapsedCount + colCount) : []
            const catLabel = cat.name

            return (
              <div key={cat.id} id={`cat-section-${cat.id}`} data-category-id={cat.id} className="mb-2 scroll-mt-24">
                {/* Category separator — like Mister York */}
                {allCats.length > 1 && (
                  <div className="flex items-center gap-4 my-6">
                    <div className="flex-1 h-px bg-kiosk-muted/40" />
                    <h2 className="text-kiosk-text font-bold text-lg tracking-wider shrink-0 uppercase">{catLabel}</h2>
                    <div className="flex-1 h-px bg-kiosk-muted/40" />
                  </div>
                )}
                {isListLayout ? (
                  /* List layouts — vertical stack, full width */
                  <div className="flex flex-col gap-2">
                    {visibleProducts.map((product) =>
                      effectiveLayout === 'text-only' ? (
                        <ProductTextRow key={product.id} product={product} onPress={() => handleProductPress(product)} />
                      ) : (
                        <ProductListRow key={product.id} product={product} onPress={() => handleProductPress(product)} />
                      ),
                    )}
                  </div>
                ) : (
                  /* Grid layouts — card-image or card-compact */
                  <div className={`grid ${cols} ${isLandscape ? 'gap-12' : 'gap-5'}`}>
                    {visibleProducts.map((product) => (
                      <ProductCard key={product.id} product={product} onPress={() => handleProductPress(product)} compact={effectiveLayout.includes('compact')} />
                    ))}
                    {/* Fyllnadsruta i luckan — endast när ALLA produkter är synliga (ingen collapse/peek) */}
                    {!hasHiddenProducts &&
                      (() => {
                        const emptySlots = (colCount - (visibleProducts.length % colCount)) % colCount
                        if (emptySlots === 0) return null
                        const pick = pickFillerTile(remoteConfig?.fillerTiles, cat.id)
                        if (!pick) return null
                        return (
                          <FillerTile
                            spanCols={emptySlots}
                            pick={pick}
                            onCategoryClick={(catName) => {
                              const targetPage = pages.find((p) => p.name.toUpperCase() === catName.toUpperCase() || p.categories.some((c) => c.name.toUpperCase() === catName.toUpperCase()))
                              if (targetPage) {
                                setViewingPage(targetPage.id)
                                scrollToTop()
                              }
                            }}
                          />
                        )
                      })()}
                  </div>
                )}
                {/* Peek row — partially visible next row with gradient fade */}
                {peekProducts.length > 0 && (
                  <div
                    className="relative mt-3 overflow-hidden"
                    style={{
                      maxHeight: isListLayout ? '60px' : useThreeCol ? '180px' : '280px',
                      WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 40%, transparent 100%)',
                      maskImage: 'linear-gradient(to bottom, black 0%, black 40%, transparent 100%)',
                    }}
                  >
                    {isListLayout ? (
                      <div className="flex flex-col gap-2 pointer-events-none">
                        {peekProducts.map((product) =>
                          effectiveLayout === 'text-only' ? (
                            <ProductTextRow key={product.id} product={product} onPress={() => {}} />
                          ) : (
                            <ProductListRow key={product.id} product={product} onPress={() => {}} />
                          ),
                        )}
                      </div>
                    ) : (
                      <div className={`grid ${cols} ${isLandscape ? 'gap-12' : 'gap-5'} pointer-events-none`}>
                        {peekProducts.map((product) => (
                          <ProductCard key={product.id} product={product} onPress={() => {}} compact={effectiveLayout.includes('compact')} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {/* "Visa alla X i kategori" — like Mister York */}
                {showCollapse && !isExpanded && (
                  <div className="flex justify-center mt-3 relative z-10 -mb-4">
                    <button
                      onClick={() => setExpandedCats((prev) => ({ ...prev, [cat.id]: true }))}
                      className="kiosk-btn px-6 py-3 rounded-full bg-kiosk-surface border-2 border-kiosk-accent text-kiosk-accent text-sm font-bold flex items-center gap-2 active:bg-kiosk-surface-2 shadow-lg"
                    >
                      {sv ? `Visa alla ${products.length} i ${catLabel}` : `Show all ${products.length} in ${catLabel}`}
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                )}
                {showCollapse && isExpanded && (
                  <div className="flex justify-center mt-3">
                    <button
                      onClick={() => setExpandedCats((prev) => ({ ...prev, [cat.id]: false }))}
                      className="kiosk-btn px-5 py-2.5 rounded-full border border-kiosk-muted/40 text-kiosk-muted text-xs font-medium flex items-center gap-2 active:bg-kiosk-surface-2"
                    >
                      {sv ? 'Visa mindre' : 'Show less'}
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Scroll hint: bouncing chevron(s) when there's more content below — placed in the gaps between tiles */}
        {hasMoreBelow && <ScrollHint cols={hintCols} gapPx={hintGap} isLandscape={isLandscape} />}

        <FloatingCart />
      </div>
    )
  }

  // ─── Category grid view (like dev.ponglindhagen.se) ───
  // Category columns: 3 default (2 or 4 if configured). Same in portrait and landscape —
  // landscape just gives each tile more breathing room.
  const configuredCols = remoteConfig?.categoryColumns
  const catGridCols = configuredCols === 2 ? 'grid-cols-2' : configuredCols === 4 ? 'grid-cols-4' : 'grid-cols-3'
  // Category-page filler (bild/video-banner längst ner)
  const catPageFiller = remoteConfig?.fillerTiles?.category_page
  const showCatPageFiller = !!catPageFiller?.enabled && !!(catPageFiller.image_url || catPageFiller.headline || catPageFiller.subtext)
  const catFillerSpan = Math.max(1, Math.min(4, catPageFiller?.span_cols ?? 2))

  return (
    <div className="h-full w-full flex flex-col bg-kiosk-bg relative">
      {/* Orange VÄLJ KATEGORI bar removed — header shows title + back in both modes */}

      {/* Category grid — columns configurable per till */}
      <div ref={scrollRef} className={`flex-1 overflow-y-auto kiosk-scroll pb-28 ${isLandscape ? 'px-14 pt-10 max-w-350 w-full mx-auto' : 'px-5 pt-3'}`}>
        <div className={`grid ${isLandscape ? 'gap-12' : 'gap-5'} ${catGridCols}`}>
          {/* "HELA MENYN" button with collage — hidden if visible === false */}
          {remoteConfig?.categoryConfigs?.['HELA MENYN']?.visible !== false && (
            <CategoryTile
              label={sv ? 'HELA MENYN' : 'FULL MENU'}
              imageUrl={null}
              collageImages={pages.slice(0, 4).map(getPageImage).filter(Boolean) as string[]}
              icon="🍽️"
              count={pages.reduce((s, p) => s + p.categories.reduce((sc, c) => sc + c.products.filter(isAvailable).length, 0), 0)}
              locale={locale}
              onPress={() => {
                setViewingPage('__all__')
                scrollToTop()
              }}
            />
          )}
          {pages.map((page) => {
            const productCount = page.categories.reduce((s, c) => s + c.products.filter(isAvailable).length, 0)
            if (productCount === 0) return null
            const pageUpper = page.name.toUpperCase()
            // Hide category if visible === false in remote config
            const catCfg = remoteConfig?.categoryConfigs?.[pageUpper] || remoteConfig?.categoryConfigs?.[page.name]
            if (catCfg?.visible === false) return null
            // Custom category image from remote config, fallback to first product image
            const customImg = catCfg?.image_url
            const img = customImg || getPageImage(page)
            const icon = catIcons[pageUpper] || '🍽️'
            return (
              <CategoryTile
                key={page.id}
                label={page.name}
                imageUrl={img}
                icon={icon}
                count={productCount}
                locale={locale}
                onPress={() => {
                  setViewingPage(page.id)
                  scrollToTop()
                }}
              />
            )
          })}
          {/* Kategorisidans fyllnadsruta (bild/video-banner, col-span konfigurerbart) —
              CSS-grid placerar den automatiskt efter sista kategorin. Om kvarvarande
              celler räcker placeras den där, annars wrap:ar den till ny rad. */}
          {showCatPageFiller && catPageFiller && (
            <CategoryPageFillerTile imageUrl={catPageFiller.image_url} headline={catPageFiller.headline} subtext={catPageFiller.subtext} spanCols={catFillerSpan} />
          )}
        </div>
      </div>

      {/* Scroll hint: bouncing chevron(s) when there's more content below — placed in the gaps between tiles */}
      {hasMoreBelow && <ScrollHint cols={hintCols} gapPx={hintGap} isLandscape={isLandscape} />}

      <FloatingCart />
    </div>
  )
}

// ─── Scroll hint: bouncing chevrons in the gaps between tiles ───
// Reads actual column count + gap from the DOM so arrows always align with real gutters:
// 1 col / list layout → 1 centered arrow
// N cols (N ≥ 2)      → N-1 arrows, one in each gutter between tiles
function ScrollHint({ cols, gapPx, isLandscape }: { cols: number; gapPx: number; isLandscape?: boolean }) {
  const halfGap = gapPx / 2
  // 1-col / list layouts have no gutters — show a single centered arrow
  if (cols < 2) {
    return (
      <div className="absolute inset-x-0 bottom-28 pointer-events-none z-30 flex justify-center">
        <ScrollHintArrow />
      </div>
    )
  }
  const gridClass = cols === 2 ? 'grid-cols-2' : cols === 3 ? 'grid-cols-3' : cols === 4 ? 'grid-cols-4' : `grid-cols-${cols}`
  return (
    <div className="absolute inset-x-0 bottom-28 pointer-events-none z-30">
      <div className={isLandscape ? 'px-14 max-w-350 mx-auto' : 'px-5'}>
        <div className={`grid ${gridClass} relative`} style={{ gap: `${gapPx}px` }}>
          {Array.from({ length: cols }).map((_, i) => (
            <div key={i} className="relative h-10">
              {i < cols - 1 && (
                <div className="absolute top-0 translate-x-1/2" style={{ right: -halfGap }}>
                  <ScrollHintArrow />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Filler tile: fills empty slots at the end of a category's product grid ───
type FillerPick =
  | { type: 'brand'; tagline: string; logoUrl?: string | null; accentColor?: string }
  | { type: 'promo'; headline: string; subtext: string; imageUrl: string }
  | { type: 'tip'; headline: string; subtext: string; imageUrl: string; category: string }

// Build a pool of enabled fillers and pick one deterministically per category
// (so it doesn't re-roll on every render but differs between categories)
function pickFillerTile(ft: FillerTilesConfig | undefined, seed: string): FillerPick | null {
  if (!ft?.enabled) return null
  const pool: FillerPick[] = []
  const store = useKioskStore.getState()
  if (ft.brand?.enabled && ft.brand.tagline) {
    pool.push({
      type: 'brand',
      tagline: ft.brand.tagline,
      logoUrl: store.remoteConfig?.logoUrl ?? null,
      accentColor: store.remoteConfig?.colorAccent,
    })
  }
  if (ft.promos?.enabled) {
    for (const p of ft.promos.items) {
      // Accept the tile if ANY of rubrik/text/media är ifyllda — bild ensam räcker
      if (p.headline || p.subtext || p.image_url) {
        pool.push({
          type: 'promo',
          headline: p.headline,
          subtext: p.subtext,
          imageUrl: p.image_url,
        })
      }
    }
  }
  if (ft.category_tips?.enabled) {
    for (const t of ft.category_tips.items) {
      if (t.headline || t.subtext || t.image_url) {
        pool.push({
          type: 'tip',
          headline: t.headline,
          subtext: t.subtext,
          imageUrl: t.image_url,
          category: t.category,
        })
      }
    }
  }
  if (pool.length === 0) return null
  // Hash the seed so each category picks a different tile but the pick is stable
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0
  return pool[Math.abs(hash) % pool.length]
}

function FillerTile({ spanCols, pick, onCategoryClick }: { spanCols: number; pick: FillerPick; onCategoryClick?: (category: string) => void }) {
  const clickable = pick.type === 'tip' && !!pick.category
  const onClick = clickable && pick.type === 'tip' ? () => onCategoryClick?.(pick.category) : undefined
  const Tag = clickable ? 'button' : 'div'

  // Common tile classes — match product cards for visual consistency
  const baseClass = 'relative bg-kiosk-surface rounded-2xl overflow-hidden flex flex-col ring-2 ring-white/25 shadow-lg shadow-black/40'

  const mediaUrl = pick.type !== 'brand' ? pick.imageUrl : ''
  const isVideo = !!mediaUrl && /\.(mp4|webm|mov|m4v)(\?|$)/i.test(mediaUrl)
  const bgMedia = mediaUrl ? (
    isVideo ? (
      <video src={mediaUrl} className="absolute inset-0 w-full h-full object-cover" autoPlay loop muted playsInline />
    ) : (
      <img src={mediaUrl} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
    )
  ) : null

  const hasMedia = !!bgMedia
  // Helper: is there any text to show for this pick?
  const hasText = pick.type === 'brand' ? !!pick.tagline : pick.type === 'tip' ? !!(pick.headline || pick.subtext || pick.category) : !!(pick.headline || pick.subtext)

  // Gradient overlay — only when we have text TO show (otherwise rent bild/video utan mörkt lager)
  const gradient = hasMedia ? (
    hasText ? (
      <div className="absolute inset-0 bg-linear-to-br from-black/70 via-black/40 to-black/70" />
    ) : null
  ) : (
    <div className="absolute inset-0 bg-linear-to-br from-kiosk-accent/30 via-kiosk-accent/10 to-kiosk-surface" />
  )

  return (
    <Tag onClick={onClick} className={`${baseClass} ${clickable ? 'kiosk-btn text-left' : ''}`} style={{ gridColumn: `span ${spanCols} / span ${spanCols}` }}>
      {bgMedia}
      {gradient}
      {hasText && (
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center p-6">
          {pick.type === 'brand' ? (
            <>
              {pick.logoUrl && <img src={pick.logoUrl} alt="" className="w-20 h-20 rounded-full object-cover mb-4 ring-2 ring-white/30 shadow-lg" />}
              <p className="text-kiosk-text font-bold text-lg leading-tight">{pick.tagline}</p>
            </>
          ) : pick.type === 'promo' ? (
            <>
              {pick.headline && <p className="text-kiosk-accent font-black text-3xl tracking-wider leading-tight">{pick.headline}</p>}
              {pick.subtext && <p className="text-kiosk-text/90 text-sm mt-2 leading-snug">{pick.subtext}</p>}
            </>
          ) : (
            /* tip */
            <>
              {pick.headline && <p className="text-kiosk-accent font-bold text-xl leading-tight">{pick.headline}</p>}
              {pick.subtext && <p className="text-kiosk-text/90 text-sm mt-1.5 leading-snug">{pick.subtext}</p>}
              {pick.category && <span className="text-kiosk-accent text-xs font-semibold mt-3 uppercase tracking-wider">{pick.category} →</span>}
            </>
          )}
        </div>
      )}
    </Tag>
  )
}

// ─── Kategorisidans fyllnadsruta ───
// Bild/video-banner som spänner över N kolumner på startsidan.
// CSS-grid placerar den naturligt efter sista kategori-tile:en.
// object-cover gör att bilden/videon alltid fyller rutan oavsett aspektförhållande
// (croppas centrerat) — innebär att ingen specifik källfil-storlek krävs.
function CategoryPageFillerTile({ imageUrl, headline, subtext, spanCols }: { imageUrl: string; headline: string; subtext: string; spanCols: number }) {
  const isVideo = !!imageUrl && /\.(mp4|webm|mov|m4v)(\?|$)/i.test(imageUrl)
  const hasMedia = !!imageUrl
  const hasText = !!(headline || subtext)

  const bgMedia = hasMedia ? (
    isVideo ? (
      <video src={imageUrl} className="absolute inset-0 w-full h-full object-cover" autoPlay loop muted playsInline />
    ) : (
      <img src={imageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
    )
  ) : null

  // Gradient: mörk över media ENDAST när vi har text; annars rent bild/video
  const gradient = hasMedia ? (
    hasText ? (
      <div className="absolute inset-0 bg-linear-to-br from-black/70 via-black/40 to-black/70" />
    ) : null
  ) : (
    <div className="absolute inset-0 bg-linear-to-br from-kiosk-accent/30 via-kiosk-accent/10 to-kiosk-surface" />
  )

  // Höjd ≈ samma som en CategoryTile (4/3 bild + text-footer ≈ 1:1 sq).
  // För col-span N: aspectRatio = N/1 → bredd = N kolumner, höjd ≈ 1 kolumn → matchar raden.
  return (
    <div
      className="relative bg-kiosk-surface rounded-2xl overflow-hidden flex flex-col ring-2 ring-white/25 shadow-lg shadow-black/40"
      style={{
        gridColumn: `span ${spanCols} / span ${spanCols}`,
        aspectRatio: `${spanCols} / 1`,
      }}
    >
      {bgMedia}
      {gradient}
      {hasText && (
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center p-8">
          {headline && <p className="text-kiosk-accent font-black text-4xl tracking-wider leading-tight">{headline}</p>}
          {subtext && <p className="text-kiosk-text/95 text-lg mt-3 leading-snug">{subtext}</p>}
        </div>
      )}
    </div>
  )
}

function ScrollHintArrow() {
  return (
    <div className="w-10 h-10 rounded-full bg-kiosk-surface/95 ring-2 ring-white/30 shadow-lg shadow-black/40 flex items-center justify-center animate-bounce">
      <svg className="w-6 h-6 text-kiosk-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  )
}

// ─── Category tile (like dev.ponglindhagen.se) ───
function CategoryTile({
  label,
  imageUrl,
  collageImages,
  icon,
  count,
  locale,
  onPress,
}: {
  label: string
  imageUrl: string | null
  collageImages?: string[]
  icon: string
  count: number
  locale: string
  onPress: () => void
}) {
  const sv = locale === 'sv'
  return (
    <button
      onClick={onPress}
      className="kiosk-btn bg-kiosk-surface rounded-2xl overflow-hidden text-center group ring-2 ring-white/25 shadow-lg shadow-black/40 hover:ring-white/40 hover:shadow-xl hover:shadow-black/50 active:shadow-md transition-all"
    >
      <div className="aspect-4/3 overflow-hidden relative">
        {collageImages && collageImages.length >= 4 ? (
          <div className="grid grid-cols-2 grid-rows-2 w-full h-full">
            {collageImages.slice(0, 4).map((img, i) => (
              <img key={i} src={img} alt="" className="w-full h-full object-cover group-active:scale-105 transition-transform" loading="lazy" />
            ))}
          </div>
        ) : imageUrl ? (
          <img src={imageUrl} alt={label} className="w-full h-full object-cover group-active:scale-105 transition-transform" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-linear-to-br from-kiosk-surface-2 to-kiosk-surface flex items-center justify-center text-5xl">{icon}</div>
        )}
        <div className="absolute inset-0 bg-linear-to-t from-kiosk-surface/90 via-transparent to-transparent" />
      </div>
      <div className="p-4">
        <p className="text-kiosk-text text-2xl font-bold leading-tight">{label}</p>
        <p className="text-kiosk-muted text-base mt-1">
          {count} {sv ? 'rätter' : 'dishes'}
        </p>
      </div>
    </button>
  )
}

// ─── Product card (card-image layout — default) ───
function ProductCard({ product, onPress, compact }: { product: Product; onPress: () => void; compact?: boolean }) {
  const locale = useKioskStore((s) => s.locale)
  const sv = locale === 'sv'
  if (product.properties == null) return null
  const props = product.properties
  const hasAlts = props.alternativeGroups && props.alternativeGroups.length > 0
  return (
    <button
      onClick={onPress}
      className="kiosk-btn bg-kiosk-surface rounded-2xl overflow-hidden text-left flex flex-col ring-2 ring-white/25 shadow-lg shadow-black/40 hover:ring-white/40 hover:shadow-xl hover:shadow-black/50 active:shadow-md transition-all"
    >
      {props.imageUrl ? (
        <div className={`${compact ? 'aspect-4/3' : 'aspect-4/3'} w-full overflow-hidden bg-kiosk-surface-2`}>
          <img src={props.imageUrl} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
        </div>
      ) : (
        <div className={`${compact ? 'aspect-3/2' : 'aspect-4/3'} w-full bg-kiosk-surface-2 flex items-center justify-center`}>
          <span className={compact ? 'text-3xl' : 'text-4xl'}>🍽</span>
        </div>
      )}
      <div className={`${compact ? 'p-2.5' : 'p-4'} flex-1 flex flex-col`}>
        {/* Name + price on same row: name left, price right */}
        <div className="flex items-start justify-between gap-3">
          <p className={`text-kiosk-text font-bold ${compact ? 'text-sm' : 'text-lg'} leading-tight line-clamp-2 flex-1 min-w-0`}>{product.name}</p>
          <div className={`shrink-0 whitespace-nowrap flex flex-col items-end leading-tight`}>
            {props.preCampaignPrice && props.price && props.preCampaignPrice > props.price && (
              <span className={`text-kiosk-muted line-through ${compact ? 'text-xs' : 'text-sm'}`}>{fmt(props.preCampaignPrice)}</span>
            )}
            <p className={`text-kiosk-accent font-bold ${compact ? 'text-sm' : 'text-lg'}`}>
              {hasAlts || !props.price ? (sv ? 'Från ' : 'From ') : ''}
              {fmt(props.price || minPrice(product))}
            </p>
          </div>
        </div>
        {!compact && props.description && <p className="text-kiosk-muted text-xs mt-2 line-clamp-2">{props.description}</p>}
      </div>
    </button>
  )
}

// ─── Product list row (horizontal row with small image) ───
function ProductListRow({ product, onPress }: { product: Product; onPress: () => void }) {
  const locale = useKioskStore((s) => s.locale)
  const sv = locale === 'sv'
  if (product.properties == null) return null
  const props = product.properties
  const hasAlts = props.alternativeGroups && props.alternativeGroups.length > 0
  return (
    <button onClick={onPress} className="kiosk-btn bg-kiosk-surface rounded-xl overflow-hidden text-left flex flex-row items-center gap-3 p-3 w-full">
      {props.imageUrl ? (
        <div className="w-20 h-20 shrink-0 rounded-lg overflow-hidden bg-kiosk-surface-2">
          <img src={props.imageUrl} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
        </div>
      ) : (
        <div className="w-20 h-20 shrink-0 rounded-lg bg-kiosk-surface-2 flex items-center justify-center">
          <span className="text-2xl">🍽</span>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-kiosk-text font-semibold text-sm leading-tight line-clamp-2">{product.name}</p>
        {props.description && <p className="text-kiosk-muted text-xs mt-1 line-clamp-1">{props.description}</p>}
      </div>
      <div className="shrink-0 pl-2 flex flex-col items-end leading-tight">
        {props.preCampaignPrice && props.price && props.preCampaignPrice > props.price && <span className="text-kiosk-muted line-through text-xs">{fmt(props.preCampaignPrice)}</span>}
        <p className="text-kiosk-accent font-bold text-base">
          {hasAlts || !props.price ? (sv ? 'Från ' : 'From ') : ''}
          {fmt(props.price || minPrice(product))}
        </p>
      </div>
    </button>
  )
}

// ─── Product text only (name + price, no image) ───
function ProductTextRow({ product, onPress }: { product: Product; onPress: () => void }) {
  const locale = useKioskStore((s) => s.locale)
  const sv = locale === 'sv'
  if (product.properties == null) return null
  const props = product.properties
  const hasAlts = props.alternativeGroups && props.alternativeGroups.length > 0
  return (
    <button onClick={onPress} className="kiosk-btn bg-kiosk-surface rounded-xl text-left flex flex-row items-center justify-between px-4 py-3.5 w-full">
      <div className="flex-1 min-w-0 pr-3">
        <p className="text-kiosk-text font-semibold text-sm leading-tight line-clamp-1">{product.name}</p>
        {props.description && <p className="text-kiosk-muted text-xs mt-0.5 line-clamp-1">{props.description}</p>}
      </div>
      <div className="shrink-0 flex flex-col items-end leading-tight">
        {props.preCampaignPrice && props.price && props.preCampaignPrice > props.price && <span className="text-kiosk-muted line-through text-xs">{fmt(props.preCampaignPrice)}</span>}
        <p className="text-kiosk-accent font-bold text-sm">
          {hasAlts || !props.price ? (sv ? 'Från ' : 'From ') : ''}
          {fmt(props.price || minPrice(product))}
        </p>
      </div>
    </button>
  )
}

/* ───── Sticky kategoribar för HELA MENYN ─────
 *
 * Horisontell scroll-bar med en knapp per kategori. Klick → smooth-scroll
 * till motsvarande sektion (id=`cat-section-{id}`). Aktiv kategori spåras
 * via IntersectionObserver och highlightas. När scroll rör sig är bar:en
 * också scrollad så aktiv knapp syns.
 */
function CategoryBar({ categories, scrollRef, align = 'left' }: { categories: { id: string; name: string }[]; scrollRef: React.RefObject<HTMLDivElement | null>; align?: 'left' | 'center' }) {
  const [activeId, setActiveId] = useState<string>(categories[0]?.id ?? '')
  const barRef = useRef<HTMLDivElement>(null)

  // IntersectionObserver för att detektera vilken kategori som syns mest just nu
  useEffect(() => {
    const root = scrollRef.current
    if (!root) return
    const sectionIds = categories.map((c) => `cat-section-${c.id}`)
    const elements = sectionIds.map((id) => document.getElementById(id)).filter((el): el is HTMLElement => el !== null)
    if (elements.length === 0) return

    // Aktivt fönster = övre 20% av scroll-area:n
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) {
          const id = visible[0].target.getAttribute('data-category-id')
          if (id) setActiveId(id)
        }
      },
      {
        root,
        rootMargin: '-10% 0px -70% 0px',
        threshold: 0,
      },
    )
    elements.forEach((el) => {
      observer.observe(el)
    })
    return () => observer.disconnect()
  }, [categories, scrollRef])

  // Auto-scroll baren så aktiv knapp syns när användaren bläddrar
  useEffect(() => {
    if (!activeId) return
    const btn = barRef.current?.querySelector<HTMLButtonElement>(`[data-cat-btn="${activeId}"]`)
    btn?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
  }, [activeId])

  function handleClick(id: string) {
    const target = document.getElementById(`cat-section-${id}`)
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Center-läget: använd inline-flex + mx-auto. När innehållet får plats
  // centreras det. Vid overflow blir det auto-vänsterställt (browser-default
  // för flex-children som är bredare än container) — scroll fungerar fortfarande.
  return (
    <div
      ref={barRef}
      className="shrink-0 border-b border-kiosk-muted/20 bg-kiosk-bg overflow-x-auto kiosk-scroll"
      style={{ scrollbarWidth: 'none', textAlign: align === 'center' ? 'center' : 'left' }}
    >
      <div className={`${align === 'center' ? 'inline-flex' : 'flex'} items-stretch gap-1 px-3 py-2 whitespace-nowrap`}>
        {categories.map((c) => {
          const isActive = activeId === c.id
          return (
            <button
              key={c.id}
              type="button"
              data-cat-btn={c.id}
              onClick={() => handleClick(c.id)}
              className={`kiosk-btn shrink-0 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                isActive ? 'bg-kiosk-accent text-white shadow' : 'bg-kiosk-surface text-kiosk-text hover:bg-kiosk-surface-2'
              }`}
            >
              {c.name.toUpperCase()}
            </button>
          )
        })}
      </div>
    </div>
  )
}
